import Carousel from "react-multi-carousel";
import {
	MouseEventHandler,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ComponentPropsWithoutRef,
	type CSSProperties,
	type PointerEventHandler,
	type ReactNode,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";

import "react-multi-carousel/lib/styles.css";

import type { Media } from "@/types";
import { cn } from "@/lib/utils";
import {
	DEMO_EMBED_FAILED_META_NAME,
	DEMO_EMBED_LAYOUT_FRAME_VALUE,
	DESKTOP_EMBED_LAYOUT_HEIGHT,
	DESKTOP_EMBED_LAYOUT_WIDTH,
	MOBILE_EMBED_LAYOUT_HEIGHT,
	MOBILE_EMBED_LAYOUT_WIDTH,
	DEMO_IFRAME_SANDBOX,
	reinforceDesktopEmbedDocument,
	getDemoEmbedProxySrc,
} from "@/lib/security/demo-embed-proxy";
import { isSafeHttpOrHttpsUrl } from "@/lib/security/safe-href";

import { Copy, Globe, Image, Link2, MessageCircle, Maximize2, Monitor, Smartphone, X } from "lucide-react";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MediaCard from "@/components/cards/MediaCard";
import { getSubsequentServiceMediaRasterSources } from "@/lib/utils/service-media-image";
import {
	ArrowButtonLeft,
	ArrowButtonRight,
	CustomButtonGroup,
	mediaCarouselArrowGlass,
	mediaCarouselChromeGlass,
} from "@/components/carousels";
import { LivePreviewScrollHintHandOverlay } from "@/components/carousels/LivePreviewScrollHintHandOverlay";
import { runLivePreviewScrollHintAfterLoad } from "@/lib/utils/live-preview-scroll-hint";
import { useToast } from "@/components/ui/use-toast";
import type { CatalogSuggestion } from "@/types";
import { ga4Share, ga4CtaClick } from "@/lib/analytics/ga4-ecommerce";

const responsive = {
	superLargeDesktop: {
		breakpoint: { max: 4000, min: 3001 },
		items: 1,
	},
	desktop: {
		breakpoint: { max: 3000, min: 1441 },
		items: 1,
	},
	laptop: {
		breakpoint: { max: 1440, min: 1281 },
		items: 1,
	},
	smallLaptop: {
		breakpoint: { max: 1280, min: 1025 },
		items: 1,
	},
	tablet: {
		breakpoint: { max: 1024, min: 673 },
		items: 1,
	},
	mobile: {
		breakpoint: { max: 672, min: 0 },
		items: 1,
	},
};

type OfferOverlay = {
	label: string;
	variant: "active" | "ended";
};

export type LiveDemoFullscreenConfig = {
	loginDetailsHtml?: string;
};

type Props = {
	media: Media[];
	shareLink: string;
	shareCatalogSuggestion?: CatalogSuggestion;
	isFavorite: boolean;
	isFavoriteLoading: boolean;
	onFavoriteClick: () => void;
	priorityFirstImage?: boolean; // For LCP optimization
	/** Mid-right offer pill (e.g. countdown) */
	offerOverlay?: OfferOverlay | null;
	onOfferOverlayClick?: () => void;
	/** Hide favorite control (e.g. basket card) — arrows and dots unchanged */
	hideToolbar?: boolean;
	/**
	 * Category / listing cards: fixed 11rem-tall media (`h-44`) instead of
	 * `aspect-video` alone, which was shorter on narrow columns.
	 */
	denseCardMedia?: boolean;
	/**
	 * Public service page: first slide is an embedded preview of the demo site (http(s) only).
	 */
	demoEmbedSrc?: string | null;
	/**
	 * When true with an active demo slide, show **only** the iframe until it errors/timeouts; then
	 * fall back to `media` slides (so the gallery is not stacked under the embed).
	 */
	demoExclusive?: boolean;
	/** Public service hero: rounded frame + shadow so the block reads above the page background */
	elevated?: boolean;
	/** Full-window live demo dialog (enlarge control); omit on listing cards etc. */
	liveDemoFullscreen?: LiveDemoFullscreenConfig | null;
	/**
	 * Basket (and similar): hide carousel chrome until the user hovers the media
	 * area or moves a pointer over it (covers touch on mobile).
	 */
	revealCarouselChromeOnHoverTouch?: boolean;
	/** Use with reveal mode when side arrows should stay visible but dots/enlarge stay hidden. */
	keepSideArrowsVisible?: boolean;
	/**
	 * Storefront live demo: load via `/api/demo-embed` and block off-origin links / popups.
	 * Disable in admin preview so validators can test raw iframe behaviour.
	 */
	restrictDemoNavigation?: boolean;
	/**
	 * Public service page: validated demo URL for the slim top ribbon (shown in images mode too).
	 * Use with `previewMode` + `onPreviewModeChange` for the Screenshots / Live Preview toggle inside the ribbon.
	 */
	liveDemoUrl?: string | null;
	previewMode?: "images" | "live-demo";
	onPreviewModeChange?: (mode: "images" | "live-demo") => void;
	/** Service detail page — gallery uses full `url` assets, not listing `displayUrl`. */
	preferOriginalImages?: boolean;
};

/** Service hero ribbon; media body is edge-to-edge inside the elevated shell (no inset). */
const MEDIA_RIBBON_ROOT_CLASS =
	"pointer-events-auto relative z-[34] flex h-8 shrink-0 items-center border-b border-focus bg-background/90 shadow-[0_2px_8px_rgba(0,0,0,0.06)] backdrop-blur-sm sm:h-9";
const MEDIA_RIBBON_PILL_TRACK_CLASS =
	"inline-flex h-7 max-w-[calc(100%-0.5rem)] items-center justify-center rounded-full bg-grey/10 !p-1 dark:bg-core-midnight sm:h-8";
type LivePreviewDeviceView = "desktop" | "mobile";
/** Listing / basket cards only — service hero media is flush to the shell edges. */
const MEDIA_RIBBON_MEDIA_PAD_CLASS = "p-3 sm:p-4";
const DENSE_CARD_MEDIA_HEIGHT_CLASS = "h-52 sm:h-44";
const DENSE_CARD_CAROUSEL_HEIGHT_CLASS =
	"h-52 aspect-auto sm:h-44 [&_.react-multi-carousel-item]:!h-52 sm:[&_.react-multi-carousel-item]:!h-44 [&_.react-multi-carousel-item>div]:!h-full [&_.react-multi-carousel-track]:!h-52 sm:[&_.react-multi-carousel-track]:!h-44";

export default function MediaCarousel({
	media,
	shareLink: rawShareLink,
	shareCatalogSuggestion,
	isFavorite,
	isFavoriteLoading,
	onFavoriteClick,
	priorityFirstImage = false,
	offerOverlay = null,
	onOfferOverlayClick,
	hideToolbar = false,
	denseCardMedia = false,
	demoEmbedSrc = null,
	demoExclusive = false,
	elevated = false,
	liveDemoFullscreen = null,
	liveDemoUrl = null,
	previewMode,
	onPreviewModeChange,
	revealCarouselChromeOnHoverTouch = false,
	keepSideArrowsVisible = false,
	restrictDemoNavigation = true,
	preferOriginalImages = false,
}: Props) {
	const { toast } = useToast();
	const carouselRef = useRef<Carousel>(null);
	const mediaShellRef = useRef<HTMLDivElement>(null);
	const [isBigPicture, showBigPicture] = useState(false);
	const [liveDemoModalOpen, setLiveDemoModalOpen] = useState(false);
	const [livePreviewDevice, setLivePreviewDevice] = useState<LivePreviewDeviceView>("desktop");
	const [carouselChromePeek, setCarouselChromePeek] = useState(false);
	const mediaChromeHoveredRef = useRef(false);
	const mediaChromeHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/** Iframe stall/error during device reloads is ignored — no client auto-disable. */
	/** Preserve page scroll when opening lightbox (avoid `scrollTo(0)` + forced teal icons under `.text-foreground`). */
	const bigPictureScrollYRef = useRef(0);
	const shareLink = useMemo(() => {
		const raw = rawShareLink.trim();
		if (!raw) return "";
		if (/^https?:\/\//i.test(raw)) return raw;
		if (typeof window === "undefined") return raw;
		return new URL(raw.startsWith("/") ? raw : `/${raw}`, window.location.origin).toString();
	}, [rawShareLink]);

	const safeDemoSrc =
		typeof demoEmbedSrc === "string" &&
		demoEmbedSrc.trim().length > 0 &&
		isSafeHttpOrHttpsUrl(demoEmbedSrc.trim())
			? demoEmbedSrc.trim()
			: null;

	const safeLiveDemoUrl =
		typeof liveDemoUrl === "string" &&
		liveDemoUrl.trim().length > 0 &&
		isSafeHttpOrHttpsUrl(liveDemoUrl.trim())
			? liveDemoUrl.trim()
			: null;

	const showPreviewModeRibbon =
		!isBigPicture &&
		safeLiveDemoUrl != null &&
		previewMode !== undefined &&
		onPreviewModeChange != null;

	/** Service page without a demo URL: same top bar, Screenshots label only (no Live Preview tab). */
	const showImagesOnlyRibbon =
		!isBigPicture && elevated && !denseCardMedia && !hideToolbar && safeLiveDemoUrl == null;

	const showTopMediaRibbon = showPreviewModeRibbon || showImagesOnlyRibbon;

	useEffect(() => {
		if (previewMode === "images") {
			setLivePreviewDevice("desktop");
		}
	}, [previewMode]);

	useEffect(() => {
		if (!revealCarouselChromeOnHoverTouch) setCarouselChromePeek(false);
	}, [revealCarouselChromeOnHoverTouch]);

	useEffect(
		() => () => {
			if (mediaChromeHideTimerRef.current) {
				clearTimeout(mediaChromeHideTimerRef.current);
			}
		},
		[],
	);

	useEffect(() => {
		if (isBigPicture) setCarouselChromePeek(false);
	}, [isBigPicture]);

	const wantsLiveDemo = previewMode === undefined || previewMode === "live-demo";
	const showDemoSlide = wantsLiveDemo && Boolean(safeDemoSrc);
	const showLivePreviewDeviceToggle =
		showDemoSlide &&
		previewMode === "live-demo" &&
		!isBigPicture &&
		!denseCardMedia;
	const galleryMedia = showDemoSlide && demoExclusive ? [] : media;
	const slideCount = (showDemoSlide ? 1 : 0) + galleryMedia.length;
	/** `infinite` with a single slide breaks react-multi-carousel (layout loops / frozen UI). */
	const carouselInfinite = slideCount > 1;
	/** Demo embed is the storefront “live site” preview — hide arrows/dots so it reads like one surface. */
	const showCarouselNav = !showDemoSlide && slideCount > 1;
	const carouselSwipeEnabled = slideCount > 1;

	/** Listing cards with one gallery image: skip react-multi-carousel so drag/swipe cannot nudge the track. */
	const staticGallerySingleSlide =
		slideCount === 1 && !showDemoSlide && galleryMedia.length === 1;
	const carouselArrowVerticalClass =
		denseCardMedia && !isBigPicture
			? "top-[6.5rem] sm:top-1/2"
			: elevated && !isBigPicture && showTopMediaRibbon
				? "top-[calc(50%_-_1rem)] sm:top-1/2"
			: "top-1/2";

	/**
	 * Live-demo-only view: `react-multi-carousel` applies transforms / list sizing that can leave a
	 * lone iframe slide with zero visible area. Render the embed outside the carousel in that case.
	 */
	const staticDemoEmbedOnly =
		showDemoSlide && Boolean(safeDemoSrc) && demoExclusive && slideCount === 1;

	/** Same control as live-demo Maximize2: opens lightbox for image carousel (Screenshots tab or no demo). */
	const showImagesEnlargeControl =
		!isBigPicture &&
		!denseCardMedia &&
		galleryMedia.length > 0 &&
		(previewMode === "images" || (previewMode === undefined && !showDemoSlide));

	const revealChrome = Boolean(revealCarouselChromeOnHoverTouch && !isBigPicture);
	/** Service page + listing cards: enlarge appears on media hover/tap, hides after idle delay. */
	const wantsTimedMediaChrome =
		!isBigPicture && !denseCardMedia && (elevated || revealCarouselChromeOnHoverTouch);

	const clearMediaChromeHideTimer = useCallback(() => {
		if (mediaChromeHideTimerRef.current) {
			clearTimeout(mediaChromeHideTimerRef.current);
			mediaChromeHideTimerRef.current = null;
		}
	}, []);

	const scheduleMediaChromeHide = useCallback(() => {
		if (!wantsTimedMediaChrome) return;
		clearMediaChromeHideTimer();
		mediaChromeHideTimerRef.current = setTimeout(() => {
			if (!mediaChromeHoveredRef.current) {
				setCarouselChromePeek(false);
			}
			mediaChromeHideTimerRef.current = null;
		}, MEDIA_CHROME_HIDE_DELAY_MS);
	}, [clearMediaChromeHideTimer, wantsTimedMediaChrome]);

	const revealTimedMediaChrome = useCallback(() => {
		if (!wantsTimedMediaChrome) return;
		clearMediaChromeHideTimer();
		setCarouselChromePeek(true);
		scheduleMediaChromeHide();
	}, [clearMediaChromeHideTimer, scheduleMediaChromeHide, wantsTimedMediaChrome]);

	const navChromeEligible = isBigPicture || showCarouselNav;
	const peekOpacityClass = (eligible: boolean) =>
		cn(
			"transition-opacity duration-200 motion-reduce:transition-none",
			!revealChrome
				? eligible
					? "pointer-events-auto opacity-100"
					: "pointer-events-none opacity-0"
				: !eligible
					? "pointer-events-none opacity-0"
					: carouselChromePeek
						? "pointer-events-auto opacity-100"
						: "pointer-events-none opacity-0",
		);

	const carouselNavChromeClass = cn("media-carousel-chrome", peekOpacityClass(navChromeEligible));
	const carouselArrowChromeClass = keepSideArrowsVisible
		? cn(
				"media-carousel-chrome transition-opacity duration-200 motion-reduce:transition-none",
				navChromeEligible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
			)
		: carouselNavChromeClass;

	const onCarouselChromePointerEnter = () => {
		if (revealChrome) setCarouselChromePeek(true);
	};
	const onCarouselChromePointerLeave = () => {
		if (revealChrome) setCarouselChromePeek(false);
	};
	const onCarouselChromePointerDownCapture: PointerEventHandler<HTMLDivElement> = (e) => {
		if (revealChrome && (e.pointerType === "touch" || e.pointerType === "pen")) {
			setCarouselChromePeek(true);
		}
	};

	const goToPreviousSlide = useCallback(() => {
		carouselRef.current?.previous(1);
	}, []);

	const goToNextSlide = useCallback(() => {
		carouselRef.current?.next(1);
	}, []);

	const sideArrowClassName = cn(
		"absolute z-40 flex size-10 -translate-y-1/2 items-center justify-center sm:size-11",
		carouselArrowVerticalClass,
		mediaCarouselArrowGlass,
		"[&>svg]:size-5 sm:[&>svg]:size-6",
		carouselArrowChromeClass,
	);

	const onModalDemoEmbedUnavailable = useCallback(() => {
		setLiveDemoModalOpen(false);
	}, []);

	/** Mobile: always show enlarge; sm+: same timed peek as other chrome. */
	const enlargeChromeClass = cn(
		"media-carousel-chrome transition-opacity duration-200 motion-reduce:transition-none",
		wantsTimedMediaChrome
			? cn(
					"pointer-events-auto opacity-100",
					!carouselChromePeek && "sm:pointer-events-none sm:opacity-0",
				)
			: peekOpacityClass(true),
	);
	const deviceToggleChromeClass = enlargeChromeClass;
	const showLiveDemoChromeOverlay =
		!isBigPicture &&
		((showLivePreviewDeviceToggle ?? false) ||
			(showDemoSlide && Boolean(safeDemoSrc) && Boolean(liveDemoFullscreen)));
	/** Iframe swallows pointer events — first tap on live preview must hit a parent-layer shim to reveal chrome. */
	const showLiveDemoTouchRevealShim =
		wantsTimedMediaChrome &&
		!carouselChromePeek &&
		!isBigPicture &&
		showDemoSlide &&
		Boolean(safeDemoSrc) &&
		showLiveDemoChromeOverlay;

	const onFavoriteButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		onFavoriteClick?.call(undefined);
	};

	const copyShareLink = async () => {
		if (!shareLink) return;
		try {
			await navigator.clipboard.writeText(shareLink);
			ga4CtaClick({
				cta_id: "media_share_copy_link",
				cta_name: "Copy link",
				cta_location: "service_media_share",
			});
			ga4Share({
				method: "copy_link",
				content_type: shareCatalogSuggestion?.type ?? "listing",
				item_id: shareCatalogSuggestion?.url,
			});
			toast({ title: "Link copied" });
		} catch {
			toast({
				title: "Could not copy link",
				description: "Please copy it from the browser address bar.",
				variant: "destructive",
			});
		}
	};

	const addShareLinkToChat = () => {
		if (!shareCatalogSuggestion) {
			void copyShareLink();
			return;
		}

		ga4CtaClick({
			cta_id: "media_share_add_to_chat",
			cta_name: "Add to chat",
			cta_location: "service_media_share",
		});
		ga4Share({
			method: "chat",
			content_type: shareCatalogSuggestion.type,
			item_id: shareCatalogSuggestion.url,
		});
		window.dispatchEvent(
			new CustomEvent("techxeed-open-support-chat", {
				detail: { catalogSuggestions: [shareCatalogSuggestion], source: "share_listing" },
			}),
		);
	};

	const renderShareMenu = () => (
		<DropdownMenu
			onOpenChange={(open) => {
				if (!open) return;
				ga4CtaClick({
					cta_id: "media_share_open",
					cta_name: "Share service",
					cta_location: "service_media_share",
				});
			}}
		>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="-mt-0.5 rounded-full text-secondary opacity-80 transition-all hover:scale-110 hover:opacity-100"
					aria-label="Share service"
					onClick={(e) => e.stopPropagation()}
				>
					<Link2 className="size-5" strokeWidth={2.25} aria-hidden />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-40">
				<DropdownMenuItem
					className="gap-2"
					onSelect={() => {
						void copyShareLink();
					}}
				>
					<Copy className="size-4 text-secondary dark:!text-secondary" aria-hidden />
					Copy link
				</DropdownMenuItem>
				<DropdownMenuItem
					className="gap-2"
					onSelect={() => {
						addShareLinkToChat();
					}}
				>
					<MessageCircle className="size-4 text-secondary dark:!text-secondary" aria-hidden />
					Add to chat
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	const openBigPicture = () => {
		bigPictureScrollYRef.current = window.scrollY;
		const root = document.body;
		root.style.overflow = "hidden";
		root.style.paddingRight = "0.25rem";
		showBigPicture(true);
	};

	const closeBigPicture = () => {
		const root = document.body;
		const y = bigPictureScrollYRef.current;
		root.style.overflow = "";
		root.style.paddingRight = "";
		showBigPicture(false);
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				window.scrollTo(0, y);
			});
		});
	};

	const bigPictureMountedRef = useRef(false);
	/**
	 * Recalc slide widths only when enlarge opens/closes. Doing this on first paint with
	 * `shouldCorrectItemPosition` freezes infinite mode at clone index 0 (often the last image).
	 */
	useLayoutEffect(() => {
		if (!bigPictureMountedRef.current) {
			bigPictureMountedRef.current = true;
			return;
		}

		const syncCarouselWidths = () => {
			const carousel = carouselRef.current as unknown as {
				state?: { slidesToShow?: number };
				setContainerAndItemWidth?: (
					slidesToShow: number,
					shouldCorrectItemPosition?: boolean,
					resetCurrentSlide?: boolean,
				) => void;
				onResize?: (value?: boolean) => void;
			} | null;
			if (!carousel) return;
			const slidesToShow = Math.max(1, carousel.state?.slidesToShow ?? 1);
			if (typeof carousel.setContainerAndItemWidth === "function") {
				carousel.setContainerAndItemWidth(slidesToShow, true);
			} else {
				carousel.onResize?.(false);
			}
		};

		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(syncCarouselWidths);
		});
		const t1 = window.setTimeout(syncCarouselWidths, 50);
		const t2 = window.setTimeout(syncCarouselWidths, 420);
		return () => {
			cancelAnimationFrame(raf);
			window.clearTimeout(t1);
			window.clearTimeout(t2);
		};
	}, [isBigPicture]);

	/** Backdrop / wrapper: tap dimmed area to close; tap to open when not fullscreen */
	const onOverlayClick = () => {
		if (isBigPicture) closeBigPicture();
		else if (!denseCardMedia) openBigPicture();
	};

	/** Listing cards wrap the carousel in a link — image taps should navigate, not enlarge. */
	const overlayOpensLightbox = !denseCardMedia || isBigPicture;

	/** Prevent inner content clicks from bubbling to overlay (which would close) */
	const onMediaShellClick: MouseEventHandler<HTMLDivElement> = (e) => {
		if (isBigPicture) e.stopPropagation();
	};

	return (
		<>
			{isBigPicture && <Dummy />}
			<div
				role="presentation"
				onClick={overlayOpensLightbox ? onOverlayClick : undefined}
				className={cn(
					/* `items-stretch` so the media shell keeps a real height for aspect-ratio / iframe embeds (center alone can collapse nested flex tracks). */
					"flex w-full items-stretch justify-center transition-all duration-500 overflow-hidden",
					elevated &&
						!isBigPicture &&
						!denseCardMedia &&
						"rounded-xl border border-border shadow-custom dark:border-border",
					denseCardMedia && !isBigPicture && cn(DENSE_CARD_MEDIA_HEIGHT_CLASS, "w-full max-w-none"),
					isBigPicture && "fixed inset-0 z-50 items-center bg-black/70 p-2 sm:p-6",
				)}
			>
				<div
					ref={mediaShellRef}
					onClick={onMediaShellClick}
					className={cn(
						"relative w-full outline-none",
						showTopMediaRibbon && !isBigPicture && "flex min-h-0 flex-col",
						isBigPicture &&
							"mx-auto flex w-full max-h-[calc(100dvh-1rem)] max-w-[min(80rem,calc((100dvh-1rem)*16/9))] items-center justify-center overflow-hidden sm:max-h-[calc(100dvh-3rem)] sm:max-w-[min(80rem,calc((100dvh-3rem)*16/9))]",
						denseCardMedia && !isBigPicture && DENSE_CARD_MEDIA_HEIGHT_CLASS,
					)}
				>
					{showPreviewModeRibbon ? (
						<div
							className={MEDIA_RIBBON_ROOT_CLASS}
							role="region"
							aria-label="Preview type"
							onClick={(e) => e.stopPropagation()}
							onPointerDown={(e) => e.stopPropagation()}
						>
							{previewMode === "images" ? (
								<Image
									className="pointer-events-none absolute left-2 top-1/2 z-[1] size-3 -translate-y-1/2 text-core-primary dark:!text-white sm:left-2.5 sm:size-3.5"
									strokeWidth={1.75}
									aria-hidden
								/>
							) : (
								<Globe
									className="pointer-events-none absolute left-2 top-1/2 z-[1] size-3 -translate-y-1/2 text-core-primary dark:!text-white sm:left-2.5 sm:size-3.5"
									strokeWidth={1.75}
									aria-hidden
								/>
							)}
							<Tabs
								value={previewMode ?? "images"}
								onValueChange={(v) =>
									onPreviewModeChange?.(v as "images" | "live-demo")
								}
								className="flex w-full justify-center px-11 sm:px-20"
							>
								<TabsList className={cn(MEDIA_RIBBON_PILL_TRACK_CLASS, "gap-0.5")}>
									<TabsTrigger
										id="service-preview-tab-images"
										value="images"
										className="group min-w-0 shrink-0 gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium leading-none sm:min-w-0 sm:px-3 sm:text-xs"
									>
										<Image
											className="size-3 shrink-0 text-core-primary group-data-[state=active]:text-white sm:size-3.5"
											strokeWidth={1.75}
											aria-hidden
										/>
										Screenshots
									</TabsTrigger>
									<TabsTrigger
										id="service-preview-tab-live-demo"
										value="live-demo"
										title="Live preview"
										className="group min-w-0 shrink-0 gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium leading-none sm:min-w-0 sm:px-3 sm:text-xs"
									>
										<Globe
											className="size-3 shrink-0 text-core-primary group-data-[state=active]:text-white sm:size-3.5"
											strokeWidth={1.75}
											aria-hidden
										/>
										<span>Live Preview</span>
									</TabsTrigger>
								</TabsList>
							</Tabs>
							{!hideToolbar ? (
								<div className="pointer-events-auto absolute right-2 top-1/2 z-40 flex -translate-y-1/2 items-center gap-2">
									{renderShareMenu()}
									<button
										type="button"
										onClick={onFavoriteButtonClick}
										className="-mt-0.5 transition-all hover:scale-110"
										aria-label={
											isFavorite ? "Remove from saved" : "Save service"
										}
									>
										<FontAwesomeIcon
											size="lg"
											icon={isFavorite ? solidHeart : solidHeart}
											className={
												isFavorite
													? "text-red-500"
													: "text-secondary opacity-70 transition-colors hover:opacity-90"
											}
											style={isFavorite ? { color: "#ef4444" } : undefined}
											aria-busy={isFavoriteLoading}
										/>
									</button>
								</div>
							) : null}
						</div>
					) : null}

					{showImagesOnlyRibbon ? (
						<div
							className={MEDIA_RIBBON_ROOT_CLASS}
							role="region"
							aria-label="Media gallery"
							onClick={(e) => e.stopPropagation()}
							onPointerDown={(e) => e.stopPropagation()}
						>
							<Image
								className="pointer-events-none absolute left-2 top-1/2 z-[1] size-3 -translate-y-1/2 text-core-primary dark:!text-white sm:left-2.5 sm:size-3.5"
								strokeWidth={1.75}
								aria-hidden
							/>
							<div className="flex w-full justify-center px-11 sm:px-20">
								<div
									className={MEDIA_RIBBON_PILL_TRACK_CLASS}
									role="group"
									aria-label="Screenshots"
								>
									<span
										id="service-preview-tab-images"
										className="inline-flex min-w-0 shrink-0 items-center justify-center gap-1 rounded-full bg-core-primary px-2.5 py-1 text-[10px] font-medium leading-none text-white shadow-sm sm:px-3 sm:text-xs"
									>
										<Image
											className="size-3 shrink-0 text-white sm:size-3.5"
											strokeWidth={1.75}
											aria-hidden
										/>
										Screenshots
									</span>
								</div>
							</div>
							{!hideToolbar ? (
								<div className="pointer-events-auto absolute right-2 top-1/2 z-40 flex -translate-y-1/2 items-center gap-2">
									{renderShareMenu()}
									<button
										type="button"
										onClick={onFavoriteButtonClick}
										className="-mt-0.5 transition-all hover:scale-110"
										aria-label={
											isFavorite ? "Remove from saved" : "Save service"
										}
									>
										<FontAwesomeIcon
											size="lg"
											icon={isFavorite ? solidHeart : solidHeart}
											className={
												isFavorite
													? "text-red-500"
													: "text-secondary opacity-70 transition-colors hover:opacity-90"
											}
											style={isFavorite ? { color: "#ef4444" } : undefined}
											aria-busy={isFavoriteLoading}
										/>
									</button>
								</div>
							) : null}
						</div>
					) : null}

					<div
						className={cn(
							"relative min-h-0 w-full",
							showDemoSlide ? "touch-pan-y overscroll-contain" : "touch-manipulation",
							showTopMediaRibbon &&
								!isBigPicture &&
								!denseCardMedia &&
								!elevated &&
								MEDIA_RIBBON_MEDIA_PAD_CLASS,
							elevated &&
								!isBigPicture &&
								!denseCardMedia &&
								"[&_.react-multi-carousel-list]:!m-0 [&_.react-multi-carousel-track]:!m-0",
						)}
						onPointerEnter={() => {
							mediaChromeHoveredRef.current = true;
							revealTimedMediaChrome();
							if (!showDemoSlide) onCarouselChromePointerEnter();
						}}
						onPointerLeave={() => {
							mediaChromeHoveredRef.current = false;
							scheduleMediaChromeHide();
							if (!showDemoSlide) onCarouselChromePointerLeave();
						}}
						onPointerDownCapture={(e) => {
							revealTimedMediaChrome();
							if (!showDemoSlide) onCarouselChromePointerDownCapture(e);
						}}
					>
						{slideCount === 0 ? (
							<div
								className={cn(
									"flex w-full items-center justify-center bg-muted/40 text-sm text-muted-foreground",
									denseCardMedia && !isBigPicture ? DENSE_CARD_MEDIA_HEIGHT_CLASS : "aspect-video",
									isBigPicture && "h-full min-h-[12rem]",
								)}
							>
								No media available
							</div>
						) : staticDemoEmbedOnly && safeDemoSrc ? (
							<div
								className={cn(
									"relative w-full min-h-0",
									denseCardMedia && !isBigPicture && DENSE_CARD_MEDIA_HEIGHT_CLASS,
									isBigPicture && "flex min-h-0 flex-1 flex-col",
								)}
							>
								<DemoSiteSlide
									key={`__service_demo_embed_${livePreviewDevice}`}
									src={safeDemoSrc}
									restrictDemoNavigation={restrictDemoNavigation}
									denseCardMedia={denseCardMedia}
									isBigPicture={isBigPicture}
									deviceView={livePreviewDevice}
								/>
							</div>
						) : staticGallerySingleSlide ? (
							<div
								className={cn(
									"relative w-full min-h-0",
									denseCardMedia && !isBigPicture && DENSE_CARD_MEDIA_HEIGHT_CLASS,
									!denseCardMedia && !isBigPicture && "aspect-video",
									isBigPicture && "flex min-h-0 flex-1 flex-col",
								)}
							>
								<MediaCard
									key={galleryMedia[0]!._id || galleryMedia[0]!.url}
									data={galleryMedia[0]!}
									className="!aspect-auto h-full min-h-0"
									fillTileHeight={denseCardMedia && !isBigPicture}
									preferOriginalImage={preferOriginalImages}
									fallbackRasterSources={getSubsequentServiceMediaRasterSources(
										galleryMedia,
										0,
										{ preferOriginal: preferOriginalImages },
									)}
									priority={priorityFirstImage}
								/>
							</div>
						) : (
							<Carousel
								ref={carouselRef}
								responsive={responsive}
								deviceType="desktop"
								ssr
								showDots={false}
								arrows={false}
								customButtonGroup={
									<CustomButtonGroup
										showArrows={false}
										itemLength={showCarouselNav ? slideCount : 1}
										infinite={carouselInfinite}
										chromeClassName={carouselNavChromeClass}
									/>
								}
								renderButtonGroupOutside={false}
								infinite={carouselInfinite}
								draggable={carouselSwipeEnabled && !showDemoSlide}
								swipeable={carouselSwipeEnabled && !showDemoSlide}
								keyBoardControl={showCarouselNav}
								className={cn(
									"relative w-full overflow-hidden",
									denseCardMedia && !isBigPicture
										? DENSE_CARD_CAROUSEL_HEIGHT_CLASS
										: cn(
												"aspect-video",
												/* Keep track/items filling the 16:9 shell so slides never letterbox. */
												"[&_.react-multi-carousel-list]:!h-full [&_.react-multi-carousel-list]:!overflow-hidden [&_.react-multi-carousel-track]:!h-full [&_.react-multi-carousel-item]:!h-full [&_.react-multi-carousel-item]:!max-w-full [&_.react-multi-carousel-item>div]:!h-full [&_.react-multi-carousel-item>div]:!w-full",
											),
									isBigPicture &&
										"max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-3rem)]",
								)}
							>
								{showDemoSlide && safeDemoSrc ? (
									<DemoSiteSlide
										key={`__service_demo_embed_${livePreviewDevice}`}
										src={safeDemoSrc}
										restrictDemoNavigation={restrictDemoNavigation}
										denseCardMedia={denseCardMedia}
										isBigPicture={isBigPicture}
										deviceView={livePreviewDevice}
									/>
								) : null}
								{galleryMedia.map((data, index) => (
									<MediaCard
										key={data._id || `${data.url}-${index}`}
										data={data}
										className="!aspect-auto h-full min-h-0"
										fillTileHeight={denseCardMedia && !isBigPicture}
										preferOriginalImage={preferOriginalImages}
										fallbackRasterSources={getSubsequentServiceMediaRasterSources(
											galleryMedia,
											index,
											{ preferOriginal: preferOriginalImages },
										)}
										priority={
											priorityFirstImage && index === 0 && !showDemoSlide
										}
									/>
								))}
							</Carousel>
						)}

						{showCarouselNav ? (
							<>
								<ArrowButtonLeft
									disabled={false}
									aria-label="Previous screenshot"
									onClick={() => {
										goToPreviousSlide();
									}}
									className={cn("left-1 sm:left-2", sideArrowClassName)}
								/>
								<ArrowButtonRight
									disabled={false}
									aria-label="Next screenshot"
									onClick={() => {
										goToNextSlide();
									}}
									className={cn("right-1 sm:right-2", sideArrowClassName)}
								/>
							</>
						) : null}

						{showLiveDemoTouchRevealShim ? (
							<button
								type="button"
								className="absolute inset-0 z-[39] m-0 cursor-default touch-manipulation appearance-none border-0 bg-transparent p-0"
								aria-label="Show live preview controls"
								onTouchStart={() => {
									revealTimedMediaChrome();
								}}
								onPointerDown={(e) => {
									if (e.pointerType === "mouse" && e.button !== 0) return;
									revealTimedMediaChrome();
								}}
							/>
						) : null}

						{!showPreviewModeRibbon && showDemoSlide && safeDemoSrc && !isBigPicture ? (
							<div
								className="pointer-events-auto absolute inset-x-0 top-0 z-[34] flex items-center justify-center gap-2 border-b border-focus bg-background/90 py-2 pl-3 pr-14 text-xs font-light text-secondary shadow-[0_2px_8px_rgba(0,0,0,0.06)] backdrop-blur-sm dark:!text-white sm:pr-20"
								role="region"
								aria-label="Live preview site"
								onClick={(e) => e.stopPropagation()}
								onPointerDown={(e) => e.stopPropagation()}
							>
								<Globe className="size-4 shrink-0" aria-hidden />
								<span className="truncate">Live Preview Site</span>
							</div>
						) : null}

						{offerOverlay && !isBigPicture && !liveDemoModalOpen ? (
							<div
								className={cn(
									"absolute right-0 z-[28] max-w-full",
									onOfferOverlayClick ? "pointer-events-auto" : "pointer-events-none",
									showTopMediaRibbon
										? "top-2"
										: showDemoSlide
											? "top-10 sm:top-12"
											: "top-2",
								)}
								aria-hidden={!onOfferOverlayClick}
							>
								{/* Media only: bookmark flush to right; ended = grey field + white copy */}
								<button
									type="button"
									disabled={!onOfferOverlayClick}
									onClick={(e) => {
										e.stopPropagation();
										onOfferOverlayClick?.();
									}}
									className={cn(
										"inline-flex max-w-full items-center justify-center whitespace-nowrap rounded-l-full rounded-r-none border-2 border-r-0 py-1.5 pl-4 pr-6 text-[10px] font-semibold uppercase leading-tight tracking-[0.06em] text-white shadow-sm sm:text-[11px]",
										onOfferOverlayClick ? "cursor-pointer" : "cursor-default",
										offerOverlay.variant === "ended"
											? "border-white/90 bg-[#c5cbd4] [text-shadow:0_1px_1px_rgba(0,0,0,0.35)] dark:border-white/80 dark:bg-zinc-500"
											: "border-white bg-[#ef3c4e]",
									)}
								>
									<span className="min-w-0">{offerOverlay.label}</span>
								</button>
							</div>
						) : null}

						{!hideToolbar && !showTopMediaRibbon && !isBigPicture ? (
							<div className="absolute right-2 top-2 z-40 flex items-center gap-2">
								{renderShareMenu()}
								<button
									type="button"
									onClick={onFavoriteButtonClick}
									className="-mt-0.5 transition-all hover:scale-110"
									aria-label={isFavorite ? "Remove from saved" : "Save service"}
								>
									<FontAwesomeIcon
										size="lg"
										icon={isFavorite ? solidHeart : solidHeart}
										className={
											isFavorite
												? "text-red-500"
												: "text-secondary opacity-70 transition-colors hover:opacity-90"
										}
										style={isFavorite ? { color: "#ef4444" } : undefined}
										aria-busy={isFavoriteLoading}
									/>
								</button>
							</div>
						) : null}

						{showLiveDemoChromeOverlay ? (
							<div
								className="pointer-events-none absolute inset-x-0 bottom-0 z-[50] isolate flex items-end justify-between gap-2 p-2"
								aria-hidden={false}
							>
								{showLivePreviewDeviceToggle ? (
									<LivePreviewDeviceToggle
										value={livePreviewDevice}
										onChange={setLivePreviewDevice}
										className={cn(
											"pointer-events-auto",
											mediaCarouselChromeGlass,
											deviceToggleChromeClass,
										)}
										onClick={(e) => e.stopPropagation()}
										onPointerDown={(e) => e.stopPropagation()}
									/>
								) : (
									<span className="size-0 shrink-0" aria-hidden />
								)}
								{showDemoSlide && safeDemoSrc && liveDemoFullscreen ? (
									<button
										type="button"
										className={cn(
											"pointer-events-auto flex size-9 items-center justify-center sm:size-10",
											mediaCarouselArrowGlass,
											"[&>svg]:size-4 sm:[&>svg]:size-5",
											enlargeChromeClass,
										)}
										aria-label="Open live preview in full window"
										onClick={(e) => {
											e.stopPropagation();
											ga4CtaClick({
												cta_id: "media_live_preview_enlarge",
												cta_name: "Open live preview in full window",
												cta_location: "service_media_carousel",
											});
											setLiveDemoModalOpen(true);
										}}
									>
										<Maximize2
											className="text-white drop-shadow-sm dark:!text-white"
											aria-hidden
										/>
									</button>
								) : null}
							</div>
						) : null}

						{showImagesEnlargeControl ? (
							<button
								type="button"
								className={cn(
									"absolute bottom-2 right-2 z-[38] flex size-9 items-center justify-center sm:size-10",
									mediaCarouselArrowGlass,
									"[&>svg]:size-4 sm:[&>svg]:size-5",
									enlargeChromeClass,
								)}
								aria-label="Enlarge images"
								onClick={(e) => {
									e.stopPropagation();
									ga4CtaClick({
										cta_id: "media_images_enlarge",
										cta_name: "Enlarge images",
										cta_location: "service_media_carousel",
									});
									openBigPicture();
								}}
							>
								<Maximize2
									className="text-white drop-shadow-sm dark:!text-white"
									aria-hidden
								/>
							</button>
						) : null}

					</div>
				</div>
			</div>

			{safeDemoSrc && liveDemoFullscreen ? (
				<Dialog open={liveDemoModalOpen} onOpenChange={setLiveDemoModalOpen}>
					<DialogContent
						className={cn(
							"live-demo-enlarge-modal",
							"!flex h-[90vh] w-[95vw] max-w-[1200px] !gap-0 flex-col overflow-hidden rounded-2xl border border-border/50 bg-background/95 p-0 shadow-lg backdrop-blur-sm dark:bg-background/95",
						)}
						hideClose
					>
						<DialogTitle className="sr-only">Live Preview Site</DialogTitle>
						<DialogDescription className="sr-only">
							Embedded demo website preview
						</DialogDescription>
						<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[inherit]">
							<div className="flex shrink-0 min-h-14 items-center justify-between gap-3 border-b border-border/50 bg-muted/35 px-3 py-2 dark:bg-muted/20 sm:px-4">
								<div className="min-w-0 flex-1">
									<div className="text-sm font-semibold text-foreground">
										Live Preview Site
									</div>
									{liveDemoFullscreen.loginDetailsHtml ? (
										<div
											className="mt-1 text-xs text-muted-foreground"
											dangerouslySetInnerHTML={{
												__html: liveDemoFullscreen.loginDetailsHtml,
											}}
										/>
									) : null}
								</div>
								<DialogClose
									className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-secondary transition-colors hover:bg-muted hover:text-tertiary dark:!text-white dark:hover:!text-white sm:size-10"
									aria-label="Close"
								>
									<X className="size-5 sm:size-6" />
									<span className="sr-only">Close</span>
								</DialogClose>
							</div>
							<div className="relative min-h-0 flex-1 bg-background">
								<DemoSiteSlide
									key={`live-demo-${liveDemoModalOpen ? "open" : "closed"}-${livePreviewDevice}`}
									src={safeDemoSrc}
									restrictDemoNavigation={restrictDemoNavigation}
									denseCardMedia={false}
									isBigPicture
									deviceView={livePreviewDevice}
									onEmbedUnavailable={onModalDemoEmbedUnavailable}
									scrollHintScope="enlarged"
									scrollHintForceReplay
								/>
								<div className="pointer-events-none absolute inset-x-0 bottom-0 z-[50] flex items-end justify-center p-2">
									<LivePreviewDeviceToggle
										value={livePreviewDevice}
										onChange={setLivePreviewDevice}
										className={cn(
											"pointer-events-auto",
											mediaCarouselChromeGlass,
											"media-carousel-chrome opacity-100",
										)}
										onClick={(e) => e.stopPropagation()}
										onPointerDown={(e) => e.stopPropagation()}
									/>
								</div>
							</div>
						</div>
					</DialogContent>
				</Dialog>
			) : null}
		</>
	);
}

/**
 * Whether a URL “allows iframe” is decided by the **remote** response headers
 * (`X-Frame-Options`, `Content-Security-Policy: frame-ancestors`, etc.). Browsers do not expose those
 * headers to our JS for cross-origin frames, and `iframe` `onLoad` may still fire when the nested
 * document is an error/blocked page — so we **cannot** preflight reliably from the client. We only
 * infer failure when `error` fires or `load` never fires within this timeout (heuristic).
 */
const DEMO_EMBED_LOAD_TIMEOUT_MS = 30_000;
/** Enlarge / live-preview chrome stays visible this long after the last hover or tap on the media area. */
const MEDIA_CHROME_HIDE_DELAY_MS = 5000;

function LivePreviewDeviceToggle({
	value,
	onChange,
	className,
	...rest
}: {
	value: LivePreviewDeviceView;
	onChange: (value: LivePreviewDeviceView) => void;
	className?: string;
} & Omit<ComponentPropsWithoutRef<"div">, "onChange">) {
	const iconClass = (active: boolean) =>
		cn(
			"pointer-events-none size-3.5 shrink-0 sm:size-4",
			active
				? "is-active !stroke-white !text-white"
				: "is-inactive !stroke-white/85 !text-white/85",
		);

	return (
		<div
			role="group"
			aria-label="Preview device"
			className={cn(
				"live-preview-device-toggle media-carousel-chrome relative grid grid-cols-2 gap-0.5 rounded-full p-0.5",
				className,
			)}
			{...rest}
		>
			<div
				aria-hidden
				className={cn(
					"pointer-events-none absolute top-0.5 bottom-0.5 w-[calc(50%-3px)] rounded-full bg-core-primary shadow-sm transition-[left] duration-200 ease-out motion-reduce:transition-none",
					value === "desktop" ? "left-0.5" : "left-[calc(50%+1px)]",
				)}
			/>
			<button
				type="button"
				aria-pressed={value === "desktop"}
				aria-label="Desktop view"
				title="Desktop view"
				className="relative z-10 inline-flex size-7 touch-manipulation items-center justify-center rounded-full sm:size-8"
				onClick={() => onChange("desktop")}
			>
				<Monitor className={iconClass(value === "desktop")} strokeWidth={2} aria-hidden />
			</button>
			<button
				type="button"
				aria-pressed={value === "mobile"}
				aria-label="Mobile view"
				title="Mobile view"
				className="relative z-10 inline-flex size-7 touch-manipulation items-center justify-center rounded-full sm:size-8"
				onClick={() => onChange("mobile")}
			>
				<Smartphone className={iconClass(value === "mobile")} strokeWidth={2} aria-hidden />
			</button>
		</div>
	);
}

function DemoEmbedViewport({
	src,
	restrictDemoNavigation,
	iframeTitle,
	variant,
	className,
	iframeClassName,
	embedViewport = "desktop",
	fitWidth,
	desktopLayoutMode = "frame",
	layoutWidth,
	layoutHeight,
	onEmbedUnavailable,
	onHandHintVisibilityChange,
	scrollHintScope,
	scrollHintForceReplay,
}: {
	src: string;
	restrictDemoNavigation: boolean;
	iframeTitle: string;
	variant: "carousel" | "modal";
	className?: string;
	iframeClassName?: string;
	embedViewport?: LivePreviewDeviceView;
	fitWidth?: number;
	/** Desktop proxy strategy: `frame` = 1280px iframe scaled by host; `meta` = viewport scale. */
	desktopLayoutMode?: "frame" | "meta";
	layoutWidth?: number;
	layoutHeight?: number;
	/** Called once on load timeout or iframe error — parent switches to image carousel + bottom demo CTA. */
	onEmbedUnavailable?: () => void;
	/** Renders hand overlay on the unscaled preview shell (not inside CSS-scaled iframe). */
	onHandHintVisibilityChange?: (visible: boolean) => void;
	/** Scroll-hint scope (`inline` vs `enlarged`) so enlarge can show the hint again. */
	scrollHintScope?: string;
	/** Always play the scroll hint when this embed loads (enlarged Live Preview modal). */
	scrollHintForceReplay?: boolean;
}) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	/** When the same-origin proxy cannot fetch the demo host, load the raw URL in the browser. */
	const [useDirectEmbed, setUseDirectEmbed] = useState(false);
	const useProxy = restrictDemoNavigation && !useDirectEmbed;
	const iframeSrc = useProxy
		? getDemoEmbedProxySrc(src, {
				viewport: embedViewport,
				layout:
					embedViewport === "desktop"
						? desktopLayoutMode === "meta"
							? "meta"
							: DEMO_EMBED_LAYOUT_FRAME_VALUE
						: undefined,
				fitWidth: embedViewport === "desktop" && desktopLayoutMode === "meta" ? fitWidth : undefined,
			})
		: src;
	const loadedRef = useRef(false);
	const unavailableNotifiedRef = useRef(false);
	/** Browser timer id; avoid `ReturnType<typeof setTimeout>` (Node `Timeout` vs DOM `number`). */
	const timeoutIdRef = useRef<number | null>(null);
	const onEmbedUnavailableRef = useRef(onEmbedUnavailable);
	const scrollHintScopeRef = useRef(scrollHintScope);
	const scrollHintForceReplayRef = useRef(scrollHintForceReplay);
	const [showInitialSpinner, setShowInitialSpinner] = useState(true);
	const compact = variant === "carousel";

	const setHandHintVisible = useCallback(
		(visible: boolean) => {
			onHandHintVisibilityChange?.(visible);
		},
		[onHandHintVisibilityChange],
	);

	useEffect(() => {
		onEmbedUnavailableRef.current = onEmbedUnavailable;
	}, [onEmbedUnavailable]);

	useEffect(() => {
		scrollHintScopeRef.current = scrollHintScope;
		scrollHintForceReplayRef.current = scrollHintForceReplay;
	}, [scrollHintScope, scrollHintForceReplay]);

	useEffect(() => {
		setUseDirectEmbed(false);
	}, [src]);

	const fireEmbedUnavailable = useCallback(() => {
		if (unavailableNotifiedRef.current) return;
		unavailableNotifiedRef.current = true;
		setShowInitialSpinner(false);
		if (timeoutIdRef.current !== null) {
			window.clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
		onEmbedUnavailableRef.current?.();
	}, []);

	/** Only depend on `src` — do not reset `loadedRef` when parent callback identity changes (would restart the 12s timer after a successful load). */
	useEffect(() => {
		loadedRef.current = false;
		unavailableNotifiedRef.current = false;
		setShowInitialSpinner(true);
		setHandHintVisible(false);
		if (timeoutIdRef.current !== null) {
			window.clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
		timeoutIdRef.current = window.setTimeout(() => {
			if (!loadedRef.current) fireEmbedUnavailable();
		}, DEMO_EMBED_LOAD_TIMEOUT_MS) as unknown as number;
		return () => {
			if (timeoutIdRef.current !== null) {
				window.clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}
		};
		// `fireEmbedUnavailable` is stable; including unstable deps would reset `loadedRef` and re-arm the stall timer after a successful load.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reload when proxy viewport mode changes
	}, [iframeSrc]);

	const onIframeLoad = () => {
		if (useProxy) {
			try {
				const doc = iframeRef.current?.contentDocument;
				const failed =
					!!doc?.querySelector(`meta[name="${DEMO_EMBED_FAILED_META_NAME}"]`) ||
					doc?.body?.getAttribute(`data-${DEMO_EMBED_FAILED_META_NAME}`) === "1";
				if (failed) {
					setUseDirectEmbed(true);
					return;
				}
			} catch {
				/* ignore */
			}
		}

		loadedRef.current = true;
		setShowInitialSpinner(false);
		if (timeoutIdRef.current !== null) {
			window.clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
		if (
			useProxy &&
			embedViewport === "desktop" &&
			desktopLayoutMode === "frame"
		) {
			try {
				const doc = iframeRef.current?.contentDocument;
				if (doc) reinforceDesktopEmbedDocument(doc);
			} catch {
				/* cross-origin or sandbox — ignore */
			}
		}
		void runLivePreviewScrollHintAfterLoad({
			iframe: iframeRef.current,
			demoSrc: src,
			embedViewport: embedViewport ?? "desktop",
			restrictDemoNavigation: useProxy,
			hintScope: scrollHintScopeRef.current,
			forceReplay: scrollHintForceReplayRef.current === true,
			onHandHintStart: () => setHandHintVisible(true),
			onHandHintEnd: () => setHandHintVisible(false),
		});
	};

	const onIframeError = () => {
		if (loadedRef.current) return;
		fireEmbedUnavailable();
	};

	const focusIframe = useCallback(() => {
		iframeRef.current?.focus({ preventScroll: true });
	}, []);

	return (
		<div
			className={cn("demo-embed-viewport relative min-h-0 w-full touch-pan-y", className)}
			onPointerEnter={focusIframe}
		>
			<iframe
				ref={iframeRef}
				key={iframeSrc}
				src={iframeSrc}
				title={iframeTitle}
				width={layoutWidth}
				height={layoutHeight}
				sandbox={DEMO_IFRAME_SANDBOX}
				scrolling="yes"
				tabIndex={0}
				style={
					layoutWidth != null && layoutHeight != null
						? { width: layoutWidth, height: layoutHeight, maxWidth: "none", border: 0 }
						: undefined
				}
				className={cn(
					"demo-embed-viewport__iframe pointer-events-auto touch-pan-y border-0",
					layoutWidth != null && layoutHeight != null
						? cn("block min-h-0 shrink-0", iframeClassName)
						: compact
							? cn("absolute inset-0 z-0 h-full w-full min-h-0", iframeClassName)
							: (iframeClassName ?? "size-full min-h-0"),
				)}
				loading="eager"
				referrerPolicy="no-referrer-when-downgrade"
				allow="clipboard-read; clipboard-write; fullscreen"
				onLoad={onIframeLoad}
				onError={onIframeError}
				onPointerDown={focusIframe}
			/>
			{showInitialSpinner ? (
				<div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80 px-4">
					<span className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>
						{compact ? "Loading preview…" : "Loading live demo…"}
					</span>
				</div>
			) : null}
		</div>
	);
}

function Dummy() {
	return (
		<div>
			<div className="aspect-video w-full" />
			<div className="h-10" />
		</div>
	);
}

function useContainerFitSize(containerRef: React.RefObject<HTMLElement | null>): {
	width: number | null;
	height: number | null;
} {
	const [fitSize, setFitSize] = useState<{ width: number; height: number } | null>(null);

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const update = () => {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) {
				setFitSize({
					width: Math.max(1, Math.round(rect.width)),
					height: Math.max(1, Math.round(rect.height)),
				});
			}
		};

		update();
		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, [containerRef]);

	return {
		width: fitSize?.width ?? null,
		height: fitSize?.height ?? null,
	};
}

function DesktopScaledDemoEmbed({
	src,
	restrictDemoNavigation,
	isBigPicture,
	shellClassName,
	frameChromeOverlay,
	onEmbedUnavailable,
	scrollHintScope,
	scrollHintForceReplay,
}: {
	src: string;
	restrictDemoNavigation: boolean;
	isBigPicture: boolean;
	shellClassName?: string;
	frameChromeOverlay?: ReactNode;
	onEmbedUnavailable?: () => void;
	scrollHintScope?: string;
	scrollHintForceReplay?: boolean;
}) {
	const shellRef = useRef<HTMLDivElement>(null);
	const [handHintVisible, setHandHintVisible] = useState(false);
	const { width: containerWidth, height: containerHeight } = useContainerFitSize(shellRef);
	const fitWidth = containerWidth;
	const scaleByWidth =
		fitWidth != null && fitWidth > 0 ? fitWidth / DESKTOP_EMBED_LAYOUT_WIDTH : 1;
	const scaleByHeight =
		isBigPicture && containerHeight != null && containerHeight > 0
			? containerHeight / DESKTOP_EMBED_LAYOUT_HEIGHT
			: scaleByWidth;
	const scale = isBigPicture ? Math.min(scaleByWidth, scaleByHeight) : scaleByWidth;
	const displayWidth = DESKTOP_EMBED_LAYOUT_WIDTH * scale;
	const scaledHeight = DESKTOP_EMBED_LAYOUT_HEIGHT * scale;

	const shellClassNameResolved = cn(
		"demo-live-preview-shell relative w-full overflow-hidden bg-muted/40",
		isBigPicture ? "h-full min-h-0 flex-1" : "aspect-video",
		shellClassName,
	);

	const scalerInnerStyle: CSSProperties = {
		width: DESKTOP_EMBED_LAYOUT_WIDTH,
		height: DESKTOP_EMBED_LAYOUT_HEIGHT,
		transform: `scale(${scale})`,
		transformOrigin: "top left",
	};

	const scaledPreview = (
		<div
			className={cn(
				"pointer-events-auto overflow-hidden",
				isBigPicture && frameChromeOverlay && "relative",
			)}
			style={{ width: displayWidth, height: scaledHeight }}
		>
			<div className="demo-desktop-embed-scaler" style={scalerInnerStyle}>
				<DemoEmbedViewport
					variant="carousel"
					src={src}
					restrictDemoNavigation={restrictDemoNavigation}
					embedViewport="desktop"
					desktopLayoutMode="frame"
					layoutWidth={DESKTOP_EMBED_LAYOUT_WIDTH}
					layoutHeight={DESKTOP_EMBED_LAYOUT_HEIGHT}
					iframeTitle="Service demo site preview"
					className="size-full min-h-0"
					iframeClassName="border-0"
					onEmbedUnavailable={onEmbedUnavailable}
					onHandHintVisibilityChange={setHandHintVisible}
					scrollHintScope={scrollHintScope}
					scrollHintForceReplay={scrollHintForceReplay}
				/>
			</div>
			{isBigPicture && frameChromeOverlay ? (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-[50] flex items-end justify-center p-2">
					<div className="pointer-events-auto">{frameChromeOverlay}</div>
				</div>
			) : null}
		</div>
	);

	return (
		<div ref={shellRef} className={shellClassNameResolved}>
			{fitWidth != null ? (
				isBigPicture ? (
					<div className="flex h-full w-full items-center justify-center overflow-hidden p-3 sm:p-4">
						{scaledPreview}
					</div>
				) : (
					<div
						className="pointer-events-auto absolute left-1/2 top-0 -translate-x-1/2 overflow-hidden"
						style={{ width: displayWidth, height: scaledHeight }}
					>
						<div className="demo-desktop-embed-scaler" style={scalerInnerStyle}>
							<DemoEmbedViewport
								variant="carousel"
								src={src}
								restrictDemoNavigation={restrictDemoNavigation}
								embedViewport="desktop"
								desktopLayoutMode="frame"
								layoutWidth={DESKTOP_EMBED_LAYOUT_WIDTH}
								layoutHeight={DESKTOP_EMBED_LAYOUT_HEIGHT}
								iframeTitle="Service demo site preview"
								className="size-full min-h-0"
								iframeClassName="border-0"
								onEmbedUnavailable={onEmbedUnavailable}
								onHandHintVisibilityChange={setHandHintVisible}
								scrollHintScope={scrollHintScope}
								scrollHintForceReplay={scrollHintForceReplay}
							/>
						</div>
					</div>
				)
			) : null}
			<LivePreviewScrollHintHandOverlay visible={handHintVisible} />
		</div>
	);
}

function MobileScaledDemoEmbed({
	src,
	restrictDemoNavigation,
	isBigPicture,
	frameChromeOverlay,
	onEmbedUnavailable,
	scrollHintScope,
	scrollHintForceReplay,
}: {
	src: string;
	restrictDemoNavigation: boolean;
	isBigPicture: boolean;
	frameChromeOverlay?: ReactNode;
	onEmbedUnavailable?: () => void;
	scrollHintScope?: string;
	scrollHintForceReplay?: boolean;
}) {
	const shellRef = useRef<HTMLDivElement>(null);
	const [handHintVisible, setHandHintVisible] = useState(false);
	const { width: containerWidth, height: containerHeight } = useContainerFitSize(shellRef);
	const scaleByWidth =
		containerWidth != null && containerWidth > 0
			? containerWidth / MOBILE_EMBED_LAYOUT_WIDTH
			: 1;
	const scaleByHeight =
		containerHeight != null && containerHeight > 0
			? containerHeight / MOBILE_EMBED_LAYOUT_HEIGHT
			: scaleByWidth;
	/** Inline: fit height (portrait). Enlarge: contain + centre like desktop. */
	const scale = isBigPicture ? Math.min(scaleByWidth, scaleByHeight) : scaleByHeight;
	const displayWidth = MOBILE_EMBED_LAYOUT_WIDTH * scale;
	const displayHeight = MOBILE_EMBED_LAYOUT_HEIGHT * scale;

	const shellClassName = cn(
		"demo-live-preview-shell relative w-full overflow-hidden bg-muted/40",
		isBigPicture ? "h-full min-h-0 flex-1" : "aspect-video",
	);

	const scalerInnerStyle: CSSProperties = {
		width: MOBILE_EMBED_LAYOUT_WIDTH,
		height: MOBILE_EMBED_LAYOUT_HEIGHT,
		transform: `scale(${scale})`,
		transformOrigin: "top left",
	};

	const scaledPreview = (
		<div
			className={cn(
				"pointer-events-auto overflow-hidden",
				isBigPicture && frameChromeOverlay && "relative",
			)}
			style={{ width: displayWidth, height: displayHeight }}
		>
			<div className="demo-mobile-embed-scaler" style={scalerInnerStyle}>
				<DemoEmbedViewport
					variant="carousel"
					src={src}
					restrictDemoNavigation={restrictDemoNavigation}
					embedViewport="mobile"
					layoutWidth={MOBILE_EMBED_LAYOUT_WIDTH}
					layoutHeight={MOBILE_EMBED_LAYOUT_HEIGHT}
					iframeTitle="Service demo site preview"
					className="size-full min-h-0"
					iframeClassName="border-0"
					onEmbedUnavailable={onEmbedUnavailable}
					onHandHintVisibilityChange={setHandHintVisible}
					scrollHintScope={scrollHintScope}
					scrollHintForceReplay={scrollHintForceReplay}
				/>
			</div>
			{isBigPicture && frameChromeOverlay ? (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-[50] flex items-end justify-center p-2">
					<div className="pointer-events-auto">{frameChromeOverlay}</div>
				</div>
			) : null}
		</div>
	);

	return (
		<div ref={shellRef} className={shellClassName}>
			{containerHeight != null ? (
				isBigPicture ? (
					<div className="flex h-full w-full items-center justify-center overflow-hidden p-3 sm:p-4">
						{scaledPreview}
					</div>
				) : (
					<div
						className="pointer-events-auto absolute left-1/2 top-0 -translate-x-1/2 overflow-hidden"
						style={{ width: displayWidth, height: displayHeight }}
					>
						<div className="demo-mobile-embed-scaler" style={scalerInnerStyle}>
							<DemoEmbedViewport
								variant="carousel"
								src={src}
								restrictDemoNavigation={restrictDemoNavigation}
								embedViewport="mobile"
								layoutWidth={MOBILE_EMBED_LAYOUT_WIDTH}
								layoutHeight={MOBILE_EMBED_LAYOUT_HEIGHT}
								iframeTitle="Service demo site preview"
								className="size-full min-h-0"
								iframeClassName="border-0"
								onEmbedUnavailable={onEmbedUnavailable}
								onHandHintVisibilityChange={setHandHintVisible}
								scrollHintScope={scrollHintScope}
								scrollHintForceReplay={scrollHintForceReplay}
							/>
						</div>
					</div>
				)
			) : null}
			<LivePreviewScrollHintHandOverlay visible={handHintVisible} />
		</div>
	);
}

function DemoSiteSlide({
	src,
	restrictDemoNavigation,
	denseCardMedia,
	isBigPicture,
	deviceView = "desktop",
	frameChromeOverlay,
	onEmbedUnavailable,
	scrollHintScope,
	scrollHintForceReplay,
}: {
	src: string;
	restrictDemoNavigation: boolean;
	denseCardMedia: boolean;
	isBigPicture: boolean;
	deviceView?: LivePreviewDeviceView;
	frameChromeOverlay?: ReactNode;
	onEmbedUnavailable?: () => void;
	scrollHintScope?: string;
	scrollHintForceReplay?: boolean;
}) {
	const useMobileVertical = deviceView === "mobile";
	const [denseHandHintVisible, setDenseHandHintVisible] = useState(false);

	if (useMobileVertical) {
		return (
			<MobileScaledDemoEmbed
				src={src}
				restrictDemoNavigation={restrictDemoNavigation}
				isBigPicture={isBigPicture}
				frameChromeOverlay={frameChromeOverlay}
				onEmbedUnavailable={onEmbedUnavailable}
				scrollHintScope={scrollHintScope}
				scrollHintForceReplay={scrollHintForceReplay}
			/>
		);
	}

	if (isBigPicture) {
		return (
			<DesktopScaledDemoEmbed
				src={src}
				restrictDemoNavigation={restrictDemoNavigation}
				isBigPicture
				frameChromeOverlay={frameChromeOverlay}
				onEmbedUnavailable={onEmbedUnavailable}
				scrollHintScope={scrollHintScope}
				scrollHintForceReplay={scrollHintForceReplay}
			/>
		);
	}

	if (denseCardMedia) {
		return (
			<div className="demo-live-preview-shell relative h-full min-h-0 w-full bg-muted/40">
				<DemoEmbedViewport
					variant="carousel"
					src={src}
					restrictDemoNavigation={restrictDemoNavigation}
					embedViewport="desktop"
					iframeTitle="Service demo site preview"
					className="absolute inset-0 min-h-0 w-full"
					iframeClassName="border-0"
					onEmbedUnavailable={onEmbedUnavailable}
					onHandHintVisibilityChange={setDenseHandHintVisible}
					scrollHintScope={scrollHintScope}
					scrollHintForceReplay={scrollHintForceReplay}
				/>
				<LivePreviewScrollHintHandOverlay visible={denseHandHintVisible} />
			</div>
		);
	}

	return (
		<DesktopScaledDemoEmbed
			src={src}
			restrictDemoNavigation={restrictDemoNavigation}
			isBigPicture={false}
			onEmbedUnavailable={onEmbedUnavailable}
			scrollHintScope={scrollHintScope}
			scrollHintForceReplay={scrollHintForceReplay}
		/>
	);
}
